import React from 'react';
import { DollarSign, ThumbsDown, Scale, Settings, Target, Info } from 'lucide-react';
import { ImpactFactors } from '../../services/RiskScoreCalculator';

interface ImpactAssessmentProps {
  data: ImpactFactors;
  onChange: (data: ImpactFactors) => void;
  calculatedScore: number;
  reasoning?: string;
}

const impactOptions = [
  { value: 1, label: 'Minimal', description: 'Negligible impact, easily managed' },
  { value: 2, label: 'Minor', description: 'Small impact, manageable with existing resources' },
  { value: 3, label: 'Moderate', description: 'Noticeable impact requiring attention and resources' },
  { value: 4, label: 'Major', description: 'Significant impact with serious consequences' },
  { value: 5, label: 'Severe', description: 'Critical impact threatening operations or viability' }
];

export const ImpactAssessment: React.FC<ImpactAssessmentProps> = ({
  data,
  onChange,
  calculatedScore,
  reasoning
}) => {
  // Check if user has modified any impact values from default (3)
  const hasUserInput = data.financial !== 3 || data.reputational !== 3 || data.regulatoryCompliance !== 3 || data.operational !== 3 || data.strategic !== 3;

  const getScoreLabel = (score: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Minimal',
      2: 'Minor',
      3: 'Moderate',
      4: 'Major',
      5: 'Severe'
    };
    return labels[score] || 'Moderate';
  };

  const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
    <span className="inline-flex items-center ml-1 text-gray-400 hover:text-gray-600 cursor-help" title={text}>
      <Info className="w-4 h-4" />
    </span>
  );

  const renderImpactDropdown = (
    label: string,
    icon: React.ReactNode,
    field: keyof ImpactFactors,
    tooltip: string,
    examples: string[]
  ) => (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:border-blue-300 transition-colors">
      <label className="block text-sm font-semibold text-gray-900 mb-3">
        <div className="flex items-center gap-2">
          {icon}
          {label}
          <InfoTooltip text={tooltip} />
        </div>
      </label>

      <select
        value={data[field]}
        onChange={(e) => onChange({ ...data, [field]: parseInt(e.target.value) as any })}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
      >
        {impactOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.value} - {option.label}: {option.description}
          </option>
        ))}
      </select>

      {/* Examples */}
      <div className="mt-3 text-xs text-gray-600 bg-gray-50 rounded p-2">
        <strong>Examples:</strong>
        <ul className="list-disc list-inside mt-1 space-y-0.5">
          {examples.map((example, idx) => (
            <li key={idx}>{example}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Target className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Impact Assessment</h3>
          <p className="text-sm text-gray-600">
            Rate each category from 1 (minimal) to 5 (severe)
          </p>
        </div>
      </div>

      {/* Impact Categories Grid */}
      <div className="grid grid-cols-1 gap-4">
        {renderImpactDropdown(
          'Financial Impact',
          <DollarSign className="w-4 h-4 text-green-600" />,
          'financial',
          'Potential monetary loss, revenue impact, or cost implications',
          [
            'Direct monetary losses',
            'Lost revenue or business opportunities',
            'Increased costs or expenses',
            'Asset devaluation'
          ]
        )}

        {renderImpactDropdown(
          'Reputational Impact',
          <ThumbsDown className="w-4 h-4 text-red-600" />,
          'reputational',
          'Effect on brand reputation, customer trust, and stakeholder confidence',
          [
            'Negative media coverage',
            'Customer trust erosion',
            'Brand value damage',
            'Stakeholder confidence loss'
          ]
        )}

        {renderImpactDropdown(
          'Regulatory/Compliance Impact',
          <Scale className="w-4 h-4 text-blue-600" />,
          'regulatoryCompliance',
          'Risk of regulatory penalties, compliance violations, or legal action',
          [
            'Regulatory fines or penalties',
            'License suspension or revocation',
            'Legal action or litigation',
            'Compliance breach consequences'
          ]
        )}

        {renderImpactDropdown(
          'Operational Impact',
          <Settings className="w-4 h-4 text-orange-600" />,
          'operational',
          'Disruption to business operations, processes, and service delivery',
          [
            'Process disruption or downtime',
            'Service delivery interruption',
            'Productivity loss',
            'Resource strain'
          ]
        )}

        {renderImpactDropdown(
          'Strategic Impact',
          <Target className="w-4 h-4 text-purple-600" />,
          'strategic',
          'Effect on long-term business strategy, goals, and competitive position',
          [
            'Strategic initiative delays',
            'Competitive advantage loss',
            'Market position erosion',
            'Long-term growth impact'
          ]
        )}
      </div>

      {/* Calculated Impact Score - Only show when user has modified values */}
      {hasUserInput ? (
        <div className="mt-8 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border-2 border-yellow-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Combined Impact Score</h4>
              <p className="text-sm text-gray-600">Average across all impact dimensions</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-orange-700">{calculatedScore}/5</div>
              <div className="text-sm text-gray-600 mt-1">{getScoreLabel(calculatedScore)}</div>
            </div>
          </div>

          {/* Impact Breakdown Visualization */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{data.financial}</div>
              <div className="text-xs text-gray-600">Financial</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{data.reputational}</div>
              <div className="text-xs text-gray-600">Reputation</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{data.regulatoryCompliance}</div>
              <div className="text-xs text-gray-600">Regulatory</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{data.operational}</div>
              <div className="text-xs text-gray-600">Operational</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{data.strategic}</div>
              <div className="text-xs text-gray-600">Strategic</div>
            </div>
          </div>

          {/* AI Reasoning */}
          {reasoning && (
            <div className="bg-white rounded-lg p-4 border border-yellow-200">
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Impact Analysis
              </h5>
              <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                {reasoning}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
          <div className="text-center text-gray-600">
            <p className="text-sm font-medium">Adjust the impact ratings above to see the calculated score</p>
            <p className="text-xs text-gray-500 mt-1">All fields are currently set to default moderate (3/5) values</p>
          </div>
        </div>
      )}
    </div>
  );
};
